import classNames from "classnames";
import React, { ReactNode } from "react";
import { Nav } from "react-bootstrap";
import { TabStripItem } from ".";
import { ChevronLeft, ChevronRight } from "../icons";
import * as bd from "../index";

interface TabStripProps {
    shade?: true | "default" | "primary" | "secondary";
    variant?: "" | "default" | "fixed" | "fullWidth" | "centered" | "vertical";
    scrollButtons?: "" | "auto" | "on" | "off" | "desktop";
    mouseWheel?: boolean;
    indicator?: "" | "none" | "reverse" | "all";
    indicatorColor?: "" | "default" | "primary" | "secondary";
    textColor?: "" | "primary" | "secondary";
    className?: string;
    children: React.ReactNode;
    [x: string]: any;
}

export const TabStrip = ({
    shade,
    variant,
    scrollButtons,
    mouseWheel = false,
    indicator,
    indicatorColor,
    textColor,
    className,
    children,
    ...props
}: TabStripProps) => {
    if (!textColor) textColor = "primary";
    if (!indicatorColor) indicatorColor = textColor;
    const cn = classNames("tabstrip", className, {
        "tabstrip-fixed": variant === "fixed",
        "tabstrip-centered": variant === "centered",
        "tabstrip-full-width": variant === "fullWidth",
        "tabstrip-indicator-none": indicator === "none",
        "tabstrip-indicator-reverse": indicator === "reverse",
        "tabstrip-indicator-all": indicator === "all",
        "tabstrip-indicator-default": indicatorColor === "default",
        "tabstrip-indicator-primary": indicatorColor === "primary",
        "tabstrip-indicator-secondary": indicatorColor === "secondary",
        "tabstrip-text-primary": textColor === "primary",
        "tabstrip-text-secondary": textColor === "secondary",
        "tabstrip-shade": shade === true || shade === "default",
        "tabstrip-shade-primary": shade === "primary",
        "tabstrip-shade-secondary": shade === "secondary",
    });
    const is_vertical = variant === "vertical";
    const getApiRef = React.createRef<any>();
    const scrollButtonsAreAuto = !scrollButtons || scrollButtons === "auto" || scrollButtons === "desktop";
    const scrollButtonsAreVisible = (api) =>
        scrollButtons === "on" || scrollButtons === "desktop" || (scrollButtonsAreAuto && (api.canGoPrev || api.canGoNext));

    const getApi = () => getApiRef.current?.();

    const getPrevButton = (api) =>
        scrollButtonsAreVisible(api) && (
            <bd.Button
                variant="text"
                color="inherit"
                size="sm"
                disabled={!api.canGoPrev}
                className={classNames("tabstrip-scroll-button", { "tabstrip-sm-hide-scroll-button": scrollButtons === "desktop" })}
                onClick={() => api.scroll(-0.5)}
            >
                <ChevronLeft className="rtl-rotate-180" />
            </bd.Button>
        );

    const getNextButton = (api) =>
        scrollButtonsAreVisible(api) && (
            <>
                <bd.Button
                    variant="text"
                    color="inherit"
                    size="sm"
                    disabled={!api.canGoNext}
                    className={classNames("tabstrip-scroll-button", { "tabstrip-sm-hide-scroll-button": scrollButtons === "desktop" })}
                    onClick={() => api.scroll(0.5)}
                >
                    <ChevronRight className="rtl-rotate-180" />
                </bd.Button>

                {/* <bd.Button variant="text" color="inherit" size="sm" className="tabstrip-scroll-button">
                    MORE
                </bd.Button> */}
            </>
        );

    const scrollTo = (elem) => getApi().scrollTo(elem);

    const MapChildren = (children: React.ReactNode) =>
        React.Children.map(children, (child) => {
            if (!child || !React.isValidElement(child)) return child;
            var x: any = child;

            if (x.type === TabStripItem) return React.cloneElement<any>(child, { scrollTo });
            if (!child.props?.children) return child;
            return React.cloneElement<any>(child, { children: MapChildren(child.props?.children) });
        });

    return (
        <bd.ScrollPane
            vertical={is_vertical}
            as={Nav}
            className={cn}
            {...props}
            getApiRef={getApiRef}
            mouseWheel={mouseWheel}
            prevButton={getPrevButton}
            nextButton={getNextButton}
        >
            {MapChildren(children)}
        </bd.ScrollPane>
    );
};
